import { useMutation } from '@tanstack/react-query'
import { queryClient } from 'App'
import useAnalytics, { AnalyticsEvents, AnalyticsMethods } from 'common/hooks/useAnalytics'
import useClearRecoilState from 'common/hooks/useClearRecoilState'
import { useClearLoginState } from 'common/hooks/useCleareLoginState'
import { setLocalStorage } from 'common/utils/localstorage'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useResetRecoilState, useSetRecoilState } from 'recoil'
import { PATH_AUTH, PATH_DASHBOARD } from 'routes/paths'

import {
  resetPassword,
  signUp,
  requestResetPasswordOTP,
  logout,
  signinSendSmsOtp,
  signinVerifySmsOtp,
} from '.'
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '../constants'
import {
  currentRegisterStepState,
  phoneNumberState,
  registerRefCodeVerifyState,
  registerStepState,
  signinOtpOrganizationIdState,
  signinOtpPhoneNumberState,
  signinOtpRefCodeState,
} from '../stores/atom'
import { SendOTPApi, SendRegisterOTPApi, VerifyOTPApi } from './types'

export const useMutateSendOTP = (fn: SendOTPApi) => {
  const { t } = useTranslation(['auth'])
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(fn, {
    onSuccess: (_) => {
      enqueueSnackbar(t('auth:send.otp.success'), {
        variant: 'success',
      })
    },
  })
}

export const useMutateRegisterSendOTP = (fn: SendRegisterOTPApi) => {
  const { t } = useTranslation(['auth'])
  const { enqueueSnackbar } = useSnackbar()

  return useMutation(fn, {
    onSuccess: (_) => {
      enqueueSnackbar(t('auth:send.otp.success'), {
        variant: 'success',
      })
    },
  })
}

export const useMutateVerifyOTP = (fn: VerifyOTPApi) => {
  return useMutation(fn)
}

export const useMutateSignUp = () => {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const { t } = useTranslation(['auth'])
  const { clearLoginState } = useClearLoginState()
  const { trackEvent } = useAnalytics()

  return useMutation(signUp, {
    onSuccess: (res) => {
      const { data } = res.data
      if (data.accessToken) {
        setLocalStorage(ACCESS_TOKEN_KEY, data.accessToken)
        setLocalStorage(REFRESH_TOKEN_KEY, data.refreshToken)
        trackEvent(AnalyticsEvents.signUp, { method: AnalyticsMethods.phoneOtp })
        navigate(PATH_DASHBOARD.root)
      }
      clearLoginState()
      enqueueSnackbar(t('auth:register.success'), { variant: 'success' })
    },
  })
}

export const useMutateResetPassword = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['auth'])

  return useMutation(resetPassword, {
    onSuccess: (_) => {
      enqueueSnackbar(t('auth:change.password.success'), { variant: 'success' })
    },
  })
}

export const useMutateRequestResetPasswordOTP = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['auth'])

  return useMutation(requestResetPasswordOTP, {
    onSuccess: (_) => {
      enqueueSnackbar(t('auth:send.otp.success'), { variant: 'success' })
    },
  })
}

export const useMutateLogout = () => {
  const clearRecoilState = useClearRecoilState()
  const navigate = useNavigate()
  const { identify, trackEvent } = useAnalytics()

  const clearStorage = () => {
    // DOF-1174: fire logout before clearing user_id so the event stays attached.
    trackEvent(AnalyticsEvents.logout)
    // DOF-1173: clear the analytics user_id on logout.
    identify(null)
    clearRecoilState()
    queryClient.clear()
    localStorage.clear()
    navigate(PATH_AUTH.login)
  }

  return useMutation(logout, {
    onSuccess: clearStorage,
    onError: clearStorage,
  })
}

export const useMutateSigninSendSmsOtp = () => {
  const setRefCode = useSetRecoilState(signinOtpRefCodeState)
  const setPhone = useSetRecoilState(signinOtpPhoneNumberState)
  const setOrgId = useSetRecoilState(signinOtpOrganizationIdState)

  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['auth'])
  const { trackEvent } = useAnalytics()

  return useMutation(signinSendSmsOtp, {
    onSuccess: (res, signinSendSmsOtp) => {
      const { data, status } = res.data
      if (status === 200) {
        setRefCode(data?.ref_code)
        enqueueSnackbar(t('send.otp.success'), { variant: 'success' })
        setPhone(signinSendSmsOtp.phone)
        if (!signinSendSmsOtp.organization_id) {
          setOrgId(signinSendSmsOtp.organization_id ?? '')
        }
        trackEvent(AnalyticsEvents.loginOtpRequested, { method: AnalyticsMethods.phoneOtp })
      }
    },
    onError: () => {
      enqueueSnackbar(t('send.otp.fail'), { variant: 'error' })
    },
  })
}

export const useMutateSigninVerifySmsOtp = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['auth'])
  const { clearLoginState } = useClearLoginState()
  const { trackEvent } = useAnalytics()

  return useMutation(signinVerifySmsOtp, {
    onSuccess: (res) => {
      const { data, status } = res.data
      if (status === 200) {
        const accessToken = data.accessToken
        setLocalStorage(ACCESS_TOKEN_KEY, accessToken)
        setLocalStorage(REFRESH_TOKEN_KEY, data.refreshToken)
        trackEvent(AnalyticsEvents.login, { method: AnalyticsMethods.phoneOtp })
        enqueueSnackbar(t('verify.otp.success'), { variant: 'success' })
        // DOF-1278: land on PATH_DASHBOARD.root, not a hardcoded page - the
        // token/permissions aren't loaded into useUserInfo yet at this
        // instant, so AuthGuard resolves the actual permitted landing page
        // once they are.
        navigate(PATH_DASHBOARD.root)
        clearLoginState()
      } else {
        enqueueSnackbar(t('verify.otp.fail'), { variant: 'error' })
      }
    },
    onError: () => {
      enqueueSnackbar(t('verify.otp.fail'), { variant: 'error' })
    },
  })
}
